import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const looksLikeJwt = (t) =>
  typeof t === "string" &&
  t &&
  t !== "undefined" &&
  t !== "null" &&
  t.split(".").length === 3;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
  "User-Timezone": getUserTimeZone(),
  ...additionalHeaders,
});

const clearAuthStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user_status");
  localStorage.removeItem("halper_remember_me");
  localStorage.removeItem("halper_saved_email");
  localStorage.removeItem("last_login_time");
  localStorage.removeItem("login_timezone");
  localStorage.removeItem("user_timezone");
  localStorage.removeItem("analyticsCurrency");
  localStorage.removeItem("analyticsCurrencyTimestamp");

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");
  sessionStorage.removeItem("last_login_time");
  sessionStorage.removeItem("login_timezone");
};

const checkUserServices = async (token) => {
  try {
    const res = await fetch(`${API_URL}/service/my-services/`, {
      headers: buildHeaders(token),
    });
    if (!res.ok) return false;
    const services = await res.json().catch(() => []);
    return Array.isArray(services) && services.length > 0;
  } catch {
    return false;
  }
};

const checkOnboardingStatus = async (token) => {
  try {
    const res = await fetch(`${API_URL}/auth/onboarding-pass-check/`, {
      method: "GET",
      headers: buildHeaders(token),
    });

    if (res.status === 200) return "COMPLETED";

    if (res.status === 400) {
      const err = await res.json().catch(() => ({}));
      const code = parseInt(
        Array.isArray(err.status) ? err.status[0] : err.status,
        10
      );
      return Number.isFinite(code) ? code : null;
    }

    return null;
  } catch {
    return null;
  }
};

const getRouteFromCode = (code, userStatus) => {
  if (userStatus === "individual") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 212:
        return "/biography-individual";
      case 214:
        return "/choose-niche";
      case 216:
        return "/my-services";
      case 218:
        return "/working-days";
      case 220:
        return "/booking-settings";
      default:
        return null;
    }
  }

  if (userStatus === "salon") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 224:
        return "/biography-salon";
      case 226:
        return "/choose-niche";
      case 228:
        return "/my-services";
      case 230:
        return "/working-days";
      case 232:
        return "/booking-settings";
      default:
        return null;
    }
  }

  if (userStatus === "worker") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 234:
        return "/worker-biography";
      case 236:
        return "/working-days";
      default:
        return null;
    }
  }

  return null;
};

export default function GoogleCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const access = params.get("access");
      const refresh = params.get("refresh");

      if (!looksLikeJwt(access) || !looksLikeJwt(refresh)) {
        clearAuthStorage();
        toast.error("Google login error");
        navigate("/sign-in", { replace: true });
        return;
      }

      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("user_status");

      localStorage.setItem("accessToken", access);
      localStorage.setItem("refreshToken", refresh);

      const tz = getUserTimeZone();
      localStorage.setItem("user_timezone", tz);
      localStorage.setItem("login_timezone", tz);
      localStorage.setItem("last_login_time", new Date().toISOString());

      let userStatus = null;
      try {
        const res = await fetch(`${API_URL}/auth/userinfo/`, {
          headers: buildHeaders(access),
        });

        if (res.status === 401) {
          clearAuthStorage();
          toast.error("Session expired. Please sign in again.");
          navigate("/sign-in", { replace: true });
          return;
        }

        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          userStatus =
            data?.user_status ||
            data?.status ||
            data?.user?.status ||
            data?.user?.user_status ||
            null;

          if (userStatus) localStorage.setItem("user_status", String(userStatus));
        }
      } catch (e) {
        console.error("Failed to fetch user status after Google login:", e);
      }

      toast.success("Signed in with Google!");

      const onboardingStatus = await checkOnboardingStatus(access);
      const hasServices = await checkUserServices(access);

      const backTo = location.state?.from;
      const safeBackTo =
        typeof backTo === "string" && backTo.startsWith("/") ? backTo : null;

      if (onboardingStatus === "COMPLETED" && hasServices) {
        navigate(safeBackTo || "/profile", { replace: true });
        return;
      }

      if (String(userStatus) === "salon" && onboardingStatus === 222) {
        try {
          await fetch(`${API_URL}/auth/update-onboarding/`, {
            method: "POST",
            headers: buildHeaders(access),
            body: JSON.stringify({ onboarding_code: 224, timeZone: tz }),
          });
        } catch {
        }
        navigate("/biography-salon", { replace: true });
        return;
      }

      if (typeof onboardingStatus === "number") {
        const route = getRouteFromCode(onboardingStatus, String(userStatus));
        if (route) {
          navigate(route, { replace: true });
          return;
        }
      }

      navigate("/account-type-select", { replace: true });
    };

    run();
  }, [navigate, location]);

  return <div>Signing in...</div>;
}